import React, { useState, useEffect } from 'react';
import { Form, Input, Upload, Button, Card, Typography, message, Space, Divider, List, Tag, Checkbox } from 'antd';
import { UploadOutlined, FolderOutlined, FileOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { formatUTCToBeijing } from '../utils/time';

const { Title, Text, Paragraph } = Typography;

const FileUpload = () => {
  const [loading, setLoading] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [directory, setDirectory] = useState('');
  const [directories, setDirectories] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [overwrite, setOverwrite] = useState(true); // 新增覆盖选项
  const navigate = useNavigate();

  const handleUpload = async (file) => {
    if (!activationCode) {
      message.error('请输入激活码');
      return false;
    }
    
    if (!directory) {
      message.error('请输入目录名');
      return false;
    }

    setLoading(true);
    
    const formData = new FormData();
    formData.append('activation_code', activationCode);
    formData.append('directory', directory);
    formData.append('file', file);
    formData.append('overwrite', overwrite); // 添加覆盖选项

    try {
      const response = await fetch('/api/v1/upload/', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        message.success(`文件上传成功: ${result.filename}`);
        setFileList([]);
        // 刷新目录列表
        fetchDirectories();
      } else {
        // 根据状态码给出不同的错误提示
        if (response.status === 401) {
          message.error('激活码无效或已过期');
        } else if (response.status === 403) {
          message.error('只有管理员才能上传文件');
        } else if (response.status === 409) {
          message.error('文件已存在，请勾选覆盖选项');
        } else if (response.status === 413) {
          message.error('文件大小超过限制（10MB）');
        } else {
          message.error(result.detail || '上传失败');
        }
      }
    } catch (error) {
      message.error('上传失败: ' + error.message);
    } finally {
      setLoading(false);
    }

    return false; // 阻止默认上传行为
  };

  const fetchDirectories = async () => {
    if (!activationCode) {
      message.warning('请先输入激活码');
      return;
    }

    try {
      const response = await fetch(`/api/v1/upload/directories?activation_code=${encodeURIComponent(activationCode)}`);
      
      if (response.ok) {
        const result = await response.json();
        setDirectories(result.directories || []);
      } else {
        const error = await response.json();
        if (response.status === 401) {
          message.error('激活码无效或已过期');
        } else if (response.status === 403) {
          message.error('只有管理员才能查看目录');
        } else {
          message.error(error.detail || '获取目录列表失败');
        }
      }
    } catch (error) {
      message.error('获取目录列表失败: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{ padding: '24px', background: '#0d1117', color: '#e6edf3', minHeight: '100vh' }}>
      <Card style={{ 
        background: '#161b22', 
        border: '1px solid #30363d',
        marginBottom: '24px'
      }}>
        <Title level={2} style={{ color: '#58a6ff', marginBottom: '16px' }}>
          文件上传管理
        </Title>
        
        <Paragraph style={{ color: '#8b949e', marginBottom: '24px' }}>
          上传文件到指定目录，需要管理员权限。支持 HTML、CSS、JSON、TXT、MD、JS 格式，HTML/JS 会进行安全脚本检查。
          {overwrite ? '默认覆盖现有文件。' : '不会覆盖现有文件。'}
        </Paragraph>

        <Form layout="vertical">
          <Form.Item label={<span style={{ color: '#e6edf3' }}>激活码</span>}>
            <Input
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value)}
              placeholder="请输入激活码"
              style={{ 
                background: '#21262d',
                border: '1px solid #30363d',
                color: '#e6edf3'
              }}
            />
          </Form.Item>

          <Form.Item label={<span style={{ color: '#e6edf3' }}>目录名</span>}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={directory}
                onChange={(e) => setDirectory(e.target.value)}
                placeholder="请输入目录名（如：static、pages等）"
                style={{ 
                  background: '#21262d',
                  border: '1px solid #30363d',
                  color: '#e6edf3'
                }}
              />
              <Button 
                onClick={fetchDirectories}
                type="primary"
                style={{ background: '#238636', border: '#238636' }}
              >
                刷新目录
              </Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item>
            <Checkbox
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              style={{ color: '#e6edf3' }}
            >
              覆盖现有文件（默认勾选）
            </Checkbox>
          </Form.Item>

          <Form.Item label={<span style={{ color: '#e6edf3' }}>选择文件</span>}>
            <Upload
              beforeUpload={handleUpload}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              multiple={false}
              showUploadList={false}
            >
              <Button 
                icon={<UploadOutlined />} 
                loading={loading}
                style={{ 
                  background: '#238636', 
                  border: '#238636',
                  color: '#ffffff'
                }}
              >
                选择文件上传
              </Button>
            </Upload>
          </Form.Item>
        </Form>
      </Card>

      {directories.length > 0 && (
        <Card style={{ 
          background: '#161b22', 
          border: '1px solid #30363d'
        }}>
          <Title level={3} style={{ color: '#58a6ff', marginBottom: '16px' }}>
            <FolderOutlined /> 目录列表
          </Title>
          
          <List
            dataSource={directories}
            renderItem={(dir) => (
              <List.Item style={{ 
                background: '#21262d', 
                marginBottom: '8px',
                padding: '16px',
                borderRadius: '6px',
                border: '1px solid #30363d'
              }}>
                <div style={{ width: '100%' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <Tag color="blue" style={{ marginBottom: '8px' }}>
                      <FolderOutlined /> {dir.name}
                    </Tag>
                  </div>
                  
                  {dir.files && dir.files.length > 0 && (
                    <div>
                      <Text style={{ color: '#8b949e', fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                        文件列表：
                      </Text>
                      {dir.files.map((file, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 8px',
                          background: '#0d1117',
                          borderRadius: '4px',
                          marginBottom: '4px'
                        }}>
                          <span style={{ color: '#e6edf3', fontSize: '12px' }}>
                            <FileOutlined style={{ marginRight: '4px' }} />
                            {file.name}
                          </span>
                          <span style={{ color: '#8b949e', fontSize: '11px' }}>
                            {formatFileSize(file.size)} • {formatUTCToBeijing(file.modified)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(!dir.files || dir.files.length === 0) && (
                    <Text style={{ color: '#8b949e', fontSize: '12px' }}>
                      目录为空
                    </Text>
                  )}
                </div>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
};

export default FileUpload;
